import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowPathIcon,
  BugAntIcon,
  QueueListIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import MultiSelect from '../../components/MultiSelect';
import WorkItemModal from './WorkItemModal';
import devopsBoardService, {
  BoardRow,
  Iteration,
  TokenStatus,
} from '../../services/devopsBoard.service';

/**
 * DevOps Backlog — everything not yet scheduled into a sprint, so it can be pulled
 * into one.
 *
 * "No sprint" is DevOps' project-root iteration path rather than a null, and assigning
 * a sprint is just writing System.IterationPath — the same edit the detail modal makes.
 */

const PRIORITY_TAG = 'top-priority';

const STATE_TONE: Record<string, string> = {
  'New': 'bg-gray-400',
  'Business Grooming': 'bg-sky-500',
  'Technical Grooming': 'bg-indigo-500',
  'Ready For Development': 'bg-teal-500',
  'Active': 'bg-amber-500',
  'Blocked': 'bg-red-500',
  'Resolved': 'bg-purple-500',
  'Closed': 'bg-green-600',
  'Removed': 'bg-gray-500',
};

/** Clickable column header that toggles direction on the active column. */
const SortHeader: React.FC<{
  label: string;
  col: 'rank' | 'created' | 'requester';
  sortKey: string;
  sortAsc: boolean;
  onSort: (k: 'rank' | 'created' | 'requester') => void;
}> = ({ label, col, sortKey, sortAsc, onSort }) => (
  <button
    className="inline-flex items-center gap-1 uppercase hover:text-gray-800 dark:hover:text-gray-200"
    onClick={() => onSort(col)}
  >
    {label}
    <span className={sortKey === col ? '' : 'opacity-0'}>{sortAsc ? '\u2191' : '\u2193'}</span>
  </button>
);

const DevOpsBacklog: React.FC = () => {
  const [rows, setRows] = useState<BoardRow[]>([]);
  const [iterations, setIterations] = useState<Iteration[]>([]);
  const [token, setToken] = useState<TokenStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [keyword, setKeyword] = useState('');
  const [states, setStates] = useState<string[]>([]);
  const [assignees, setAssignees] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [requesters, setRequesters] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkSprint, setBulkSprint] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [openWorkItemId, setOpenWorkItemId] = useState<number | null>(null);
  // 'rank' is DevOps' own backlog order; the other keys are Lisa's triage views.
  const [sortKey, setSortKey] = useState<'rank' | 'created' | 'requester'>('rank');
  const [sortAsc, setSortAsc] = useState(true);

  const canEdit = Boolean(token?.connected);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [backlog, tok] = await Promise.all([
        devopsBoardService.getBacklog(),
        devopsBoardService.getTokenStatus(),
      ]);
      setRows(backlog.data);
      // Only real sprints are assignment targets; the project root IS the backlog.
      setIterations(backlog.iterations.filter(i => i.path.includes('\\')));
      setToken(tok);
    } catch {
      setError('Could not load the backlog from Azure DevOps.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const allStates = useMemo(
    () => Array.from(new Set(rows.map(r => r.state))).sort(), [rows]);
  const allAssignees = useMemo(
    () => Array.from(new Set(rows.map(r => r.assignedTo ?? 'Unassigned'))).sort(), [rows]);
  const allTypes = useMemo(
    () => Array.from(new Set(rows.map(r => r.type))).sort(), [rows]);
  const allRequesters = useMemo(
    () => Array.from(new Set(rows.map(r => r.requester?.name ?? 'Unknown'))).sort(), [rows]);

  const visible = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const matched = rows.filter(r => {
      if (states.length && !states.includes(r.state)) return false;
      if (types.length && !types.includes(r.type)) return false;
      if (assignees.length && !assignees.includes(r.assignedTo ?? 'Unassigned')) return false;
      if (requesters.length && !requesters.includes(r.requester?.name ?? 'Unknown')) return false;
      if (kw && !`${r.id} ${r.title} ${r.requester?.name ?? ''} ${r.requester?.email ?? ''}`
        .toLowerCase().includes(kw)) return false;
      return true;
    });

    const direction = sortAsc ? 1 : -1;
    return [...matched].sort((a, b) => {
      if (sortKey === 'created') {
        // Undated items sort last either way rather than jumping to the top.
        const at = a.createdDate ? Date.parse(a.createdDate) : null;
        const bt = b.createdDate ? Date.parse(b.createdDate) : null;
        if (at === null && bt === null) return 0;
        if (at === null) return 1;
        if (bt === null) return -1;
        return (at - bt) * direction;
      }
      if (sortKey === 'requester') {
        return (a.requester?.name ?? 'Unknown')
          .localeCompare(b.requester?.name ?? 'Unknown') * direction;
      }
      return 0; // 'rank' — the server already returned stack-rank order.
    });
  }, [rows, keyword, states, types, assignees, requesters, sortKey, sortAsc]);

  const filtersOn = keyword !== '' || states.length > 0
    || assignees.length > 0 || types.length > 0 || requesters.length > 0;

  /** Move one item into a sprint; it then leaves the backlog. */
  const assign = async (id: number, iterationPath: string) => {
    if (!iterationPath) return;
    setBusyId(id);
    setError(null);
    try {
      await devopsBoardService.updateWorkItem(id, { iterationPath });
      setRows(rs => rs.filter(r => r.id !== id));
      setSelected(s => { const n = new Set(s); n.delete(id); return n; });
      const name = iterations.find(i => i.path === iterationPath)?.name ?? iterationPath;
      setNotice(`${id} moved to ${name}.`);
      setTimeout(() => setNotice(null), 3000);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? `Could not move ${id} into that sprint.`);
    } finally {
      setBusyId(null);
    }
  };

  const assignSelected = async () => {
    if (!bulkSprint || selected.size === 0) return;
    const ids = Array.from(selected);
    setError(null);

    // Sequential rather than parallel: a partial failure should leave a clear picture
    // of what moved, and it keeps us off DevOps' rate limits.
    const moved: number[] = [];
    for (const id of ids) {
      setBusyId(id);
      try {
        await devopsBoardService.updateWorkItem(id, { iterationPath: bulkSprint });
        moved.push(id);
      } catch {
        setError(`Stopped after ${moved.length} of ${ids.length}. ${id} could not be moved.`);
        break;
      }
    }
    setBusyId(null);
    if (moved.length) {
      setRows(rs => rs.filter(r => !moved.includes(r.id)));
      setSelected(new Set());
      const name = iterations.find(i => i.path === bulkSprint)?.name ?? bulkSprint;
      setNotice(`Moved ${moved.length} item${moved.length === 1 ? '' : 's'} to ${name}.`);
      setTimeout(() => setNotice(null), 4000);
    }
  };

  const toggle = (id: number) =>
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const allVisibleSelected = visible.length > 0 && visible.every(r => selected.has(r.id));

  const control = `rounded border border-gray-300 dark:border-gray-600 bg-white
                   dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100`;

  return (
    <div className="p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-body">
          DevOps Backlog
        </h1>
        <button
          onClick={load}
          className={`inline-flex items-center gap-1 ${control} hover:bg-gray-50 dark:hover:bg-gray-700`}
          aria-label="Refresh backlog"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        {sortKey !== 'rank' && (
          <button
            onClick={() => { setSortKey('rank'); setSortAsc(true); }}
            className="text-sm text-blue-600 underline dark:text-blue-400"
          >
            Back to backlog order
          </button>
        )}
        <span className="ml-auto text-sm text-muted">
          {visible.length} of {rows.length} unscheduled
        </span>
      </div>

      {!canEdit && !loading && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm
                        text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
          Read-only. Connect your Azure DevOps token on the Kanban board to move items
          into sprints.
        </div>
      )}
      {error && (
        <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm
                        text-red-800 dark:border-red-700 dark:bg-red-900/20 dark:text-red-200">
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded border border-green-300 bg-green-50 px-3 py-2 text-sm
                        text-green-800 dark:border-green-700 dark:bg-green-900/20 dark:text-green-200">
          {notice}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <input
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          placeholder="Filter by keyword or id"
          className={`${control} w-56`}
        />
        <div className="w-52">
          <MultiSelect options={allStates.map(s => ({ value: s, label: s }))}
            value={states} onChange={setStates} placeholder="All states"
            size="sm" usePortal enableSearch />
        </div>
        <div className="w-52">
          <MultiSelect options={allAssignees.map(a => ({ value: a, label: a }))}
            value={assignees} onChange={setAssignees} placeholder="All people"
            size="sm" usePortal enableSearch />
        </div>
        <div className="w-40">
          <MultiSelect options={allTypes.map(t => ({ value: t, label: t }))}
            value={types} onChange={setTypes} placeholder="All types" size="sm" usePortal />
        </div>
        <div className="w-52">
          <MultiSelect options={allRequesters.map(r => ({ value: r, label: r }))}
            value={requesters} onChange={setRequesters} placeholder="All requesters"
            size="sm" usePortal enableSearch />
        </div>
        {filtersOn && (
          <button
            onClick={() => { setKeyword(''); setStates([]); setAssignees([]); setTypes([]); setRequesters([]); }}
            className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-800
                       dark:text-gray-400 dark:hover:text-gray-200"
          >
            <XMarkIcon className="h-4 w-4" /> Clear
          </button>
        )}
      </div>

      {/* Bulk assign */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded border border-blue-300
                        bg-blue-50 px-3 py-2 text-sm dark:border-blue-700 dark:bg-blue-900/20">
          <span className="text-blue-900 dark:text-blue-200">
            {selected.size} selected
          </span>
          <select
            className={control}
            value={bulkSprint}
            onChange={e => setBulkSprint(e.target.value)}
          >
            <option value="">Choose a sprint…</option>
            {iterations.map(i => <option key={i.id} value={i.path}>{i.name}</option>)}
          </select>
          <button
            className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50"
            disabled={!canEdit || !bulkSprint || busyId !== null}
            onClick={assignSelected}
          >
            {busyId !== null ? 'Moving…' : 'Add to sprint'}
          </button>
          <button
            className="text-blue-800 underline dark:text-blue-300"
            onClick={() => setSelected(new Set())}
          >
            Clear selection
          </button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="py-10 text-center text-muted">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="py-10 text-center text-muted">
          {rows.length === 0 ? 'Nothing is sitting in the backlog.' : 'No items match these filters.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1050px] text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase
                             text-gray-500 dark:border-gray-700 dark:text-gray-400">
                <th className="w-8 py-1">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    aria-label="Select all shown"
                    onChange={e => setSelected(
                      e.target.checked ? new Set(visible.map(r => r.id)) : new Set(),
                    )}
                  />
                </th>
                <th className="py-1">Item</th>
                <th className="w-44 py-1">
                  <SortHeader label="Requested By" col="requester"
                    sortKey={sortKey} sortAsc={sortAsc}
                    onSort={k => { setSortAsc(sortKey === k ? !sortAsc : true); setSortKey(k); }} />
                </th>
                <th className="w-28 py-1">
                  <SortHeader label="Received" col="created"
                    sortKey={sortKey} sortAsc={sortAsc}
                    onSort={k => { setSortAsc(sortKey === k ? !sortAsc : true); setSortKey(k); }} />
                </th>
                <th className="w-36 py-1">State</th>
                <th className="w-40 py-1">Assigned To</th>
                <th className="w-52 py-1">Add to sprint</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(row => (
                <tr
                  key={row.id}
                  className={`border-b border-gray-100 dark:border-gray-800 ${
                    row.tags.includes(PRIORITY_TAG) ? 'bg-orange-50 dark:bg-orange-900/20' : ''
                  }`}
                >
                  <td className="py-1.5">
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      aria-label={`Select ${row.id}`}
                      onChange={() => toggle(row.id)}
                    />
                  </td>
                  <td className="py-1.5">
                    <div className="flex items-start gap-1">
                      {row.type === 'Bug'
                        ? <BugAntIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
                        : <QueueListIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-500" />}
                      <button
                        onClick={() => setOpenWorkItemId(row.id)}
                        className="font-semibold text-blue-600 hover:underline dark:text-blue-400"
                      >
                        {row.id}
                      </button>
                      <span className="text-gray-800 dark:text-gray-100">{row.title}</span>
                    </div>
                    {row.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 pl-5 pt-0.5">
                        {row.tags.map(t => (
                          <span key={t} className={`rounded px-1.5 py-0.5 text-[0.625rem] ${
                            t === PRIORITY_TAG
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200'
                          }`}>{t}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="py-1.5 text-gray-600 dark:text-gray-300">
                    {row.requester ? (
                      <span title={row.requester.email}>{row.requester.name}</span>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">Unknown</span>
                    )}
                  </td>
                  <td className="py-1.5 text-gray-600 dark:text-gray-300">
                    {row.createdDate
                      ? new Date(row.createdDate).toLocaleDateString()
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="py-1.5 text-gray-600 dark:text-gray-300">
                    <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${
                      STATE_TONE[row.state] ?? 'bg-gray-400'}`} />
                    {row.state}
                  </td>
                  <td className="py-1.5 text-gray-600 dark:text-gray-300">
                    {row.assignedTo ?? 'Unassigned'}
                  </td>
                  <td className="py-1.5">
                    <select
                      className={`${control} w-full`}
                      value=""
                      disabled={!canEdit || busyId === row.id}
                      onChange={e => assign(row.id, e.target.value)}
                    >
                      <option value="">
                        {busyId === row.id ? 'Moving…' : 'Choose a sprint…'}
                      </option>
                      {iterations.map(i => (
                        <option key={i.id} value={i.path}>{i.name}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openWorkItemId !== null && (
        <WorkItemModal
          workItemId={openWorkItemId}
          canEdit={canEdit}
          onClose={() => setOpenWorkItemId(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default DevOpsBacklog;
